/*
<summary>
   This functional component is all about rendering the Tool BOM list/grid.
   Developer: Aashish Singh, Created Date: 07-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import toast from 'react-hot-toast';
import Header from '../../shared-components/header/header';
import Grid from '../../shared-components/Grid/Grid';
import { CustomActionFormatter } from '../../shared-components/Grid/GridFormatter';
import Pagination from '../../shared-components/Grid/Pagination';
import DeletePopup from '../../shared-components/popup/delete/delete-popup';
import DeletePopupHOC from '../../shared-components/popup/delete/delete-popup-hoc';
import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
  RefreshIcon,
  ImportHolidayIcon,
} from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import './tool-bom.css';
import { ModuleType } from '../../constants/enums/module-type-enum';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import {
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from '../../helpers/action-permission-helper';
import AddEditToolBOM from './components/add-edit-tool-bom';
import ImportToolBOM from './components/import-tool-bom';
import { useStore } from '../../contexts/store-provider';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import IPageInfo, { IPageLimitInfo } from '../../models/ICommon';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import AddToolBOMPopupHOC from './hoc/add-tool-bom-popup-hoc';
import ImportToolBOMPopupHOC from './hoc/import-tool-bom-popup-hoc';
import { GridRemoteConstant } from '../../constants/grid-constant';
import noMessageIcon from '../../assets/Images/filterNodata.png';
import {
  getTenantIdFromLocalStorage,
  clearToolBOMFactoryIdFromLocalStorage,
} from '../../helpers/localstorage-helper';
import RoutesConstants from '../../shared-components/routes/routes-constants';
import { useHistory } from 'react-router-dom';

/**
* This functional component is used to render the Tool BOM list grid along with add, edit and delete actions.
* @param props: Component props containing popup toggle handlers passed by the wrapping HOCs
*/
const ToolBOM = (props: any) => {
  const { preferencesStore, toolBOMStore } = useStore();
  const {
    userSetting,
    setUserSetting,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    toolBOMList,
    GetToolBOMListService,
    GetFactoryIdByTenantIdService,
    getAllToolBOMState,
    DeleteToolBOMService,
    resetDeleteState,
    deleteToolBOMState,
    getToolBOMList,
    fileUploadState,
    resetFileUploadState,
    importResults,
    resetToolBOMList
  } = toolBOMStore;
  const isFirstLoad = useRef(true);
  const history = useHistory();
  const [selectedId, setSelectedId] = useState(-1);
  const [selectedToolModel, setSelectedToolModel] = useState('');
  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>(
    { dataField: '', order: '' } as IDefaultColumSortOrder
  );

  const pageLimitOptions: Array<number> = [10, 20, 50, 100];
  const [disablePagination, setDisablePagination] = useState<boolean>(false);

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  /**
  * This function is used to call the Tool BOM list API with the current paging state.
  * @param filterApplied: Flag indicating the page should be reset to 1
  */
  const getToolBOMListServiceHandler = (filterApplied?: boolean) => {
    GetToolBOMListService(
      filterApplied ? 1 : pageInfo.currentPage,
      pageLimitInfo.pageLimit
    );
  };

  /**
   * This useEffect is used to resolve this tenant's factory_id (DB name) on initial mount before
   * loading the Tool BOM list — every Tool BOM CRUD call is tenant-DB-scoped by factory_id, not
   * by the numeric tenant id, so it must be resolved and cached first. The cached factory_id is
   * cleared again on unmount, i.e. once the user navigates away from the Tool BOM module.
   */
  useEffect(() => {
    GetFactoryIdByTenantIdService(Number(getTenantIdFromLocalStorage())).then(() => {
      getToolBOMListServiceHandler();
    });

    return () => {
      resetToolBOMList()
      clearToolBOMFactoryIdFromLocalStorage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This useEffect is used to sync the current page and total pages with the latest Tool BOM list response.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      currentPage: toolBOMList?.PagingDetails?.CurrentPage,
      totalPages: toolBOMList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolBOMList]);

  /**
   * This useEffect is used to reload the list whenever the current page changes via the pager.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      getToolBOMListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * This useEffect is used to reload the list whenever the page size selection changes.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      getToolBOMListServiceHandler();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This useEffect is used to show a success toast, close the delete popup and refresh the list after a successful delete.
   */
  useEffect(() => {
    if (deleteToolBOMState.success) {
      toast.success(formatMessage('deleted_success'));
      props.deletePopupToggleHandler();
      handleRefresh();
      resetDeleteState();
    }
  }, [deleteToolBOMState.success]);

  /**
   * This useEffect is used to close the add/edit popup and refresh the list after a successful add or update.
   */
  useEffect(() => {
    if (toolBOMStore.addUpdateToolBOMState.success) {
      props.addToggleHandler();
      handleRefresh();
      toolBOMStore.resetAddUpdateState();
    }
  }, [toolBOMStore.addUpdateToolBOMState.success]);

  /**
   * This useEffect is used to reload the list and reset the language changed flag whenever the
   * selected language changes.
   */
  useEffect(() => {
    if (islanguageChanged) {
      getToolBOMListServiceHandler();
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  /**
   * This useEffect is used to show a success toast and reset the file upload state once a
   * CSV import completes successfully. The popup itself closes via ImportToolBOM's own effect.
   */
  useEffect(() => {
    if (fileUploadState.success) {
      toast.success(formatMessage('ImportSuccess'));
      resetFileUploadState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadState.success]);

  /**
   * This useEffect is used to refresh the grid after a CSV import response that inserted at
   * least one row — driven off importResults (not fileUploadState.success/.error) so it fires
   * reliably exactly once per response, including on a partial-success import.
   */
  useEffect(() => {
    if (importResults.length > 0 && importResults.some((result) => result.Success)) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importResults]);

  const handleRefresh = () => {
    getToolBOMListServiceHandler();
  };

  const openAddModal = () => {
    setSelectedId(-1);
    props.addToggleHandler();
  };

  const handleEdit = (row: any, event: any) => {
    setSelectedId(row.Id);
    event.stopPropagation();
    props.addToggleHandler();
  };

  const handleDeleteClick = (row: any, event: any) => {
    setSelectedId(row.Id);
    setSelectedToolModel(row.ToolModel || '');
    event.stopPropagation();
    props.deletePopupToggleHandler();
  };

  const deleteHandler = () => {
    DeleteToolBOMService(selectedId, selectedToolModel, Number(getTenantIdFromLocalStorage()));
  };

  /**
  * This function is used to close the add/edit popup and refresh the list if the add/update was successful.
  */
  const closeModal = () => {
    props.addToggleHandler();
    if (toolBOMStore.addUpdateToolBOMState.success) {
      handleRefresh();
      toolBOMStore.resetAddUpdateState();
    }
  };

  const openImportModal = (event: any) => {
    event.stopPropagation();
    props.importToggleHandler();
  };

  const actionIcons: any[] = showIconsRoleBase(
    [
      {
        dataField: 'isEdit',
        handler: handleEdit,
        icon: <EditIcon />,
        isDisable: !isOperationPermittedRoleBase(ModuleType.ToolBOM, 'Edit'),
        title: 'edit',
        varient: 'primary',
        action: 'edit',
      },
      {
        dataField: 'isDelete',
        handler: handleDeleteClick,
        icon: <DeleteIcon />,
        isDisable: !isOperationPermittedRoleBase(ModuleType.ToolBOM, 'Delete'),
        title: 'delete',
        varient: 'danger',
        action: 'delete',
      },
    ],
    ModuleType.ToolBOM
  );

  const columns: any = [
    {
      dataField: 'serial_number',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses tool-bom-col-sr-no',
      headerClasses: 'tool-bom-col-sr-no',
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
    },
    {
      dataField: 'MaterialProductId',
      text: formatMessage('material_product_id'),
      classes: 'show-Elipses tool-bom-col-product-id',
      headerClasses: 'tool-bom-col-product-id',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: 'MaterialKatashiki1',
      text: formatMessage('material_katashiki'),
      classes: 'show-Elipses',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: 'Category',
      text: formatMessage('category'),
      classes: 'show-Elipses',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: 'MaterialManufacturerCode',
      text: formatMessage('manufacturer_name'),
      classes: 'show-Elipses',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: 'TecmibizCode',
      text: formatMessage('tmb_model'),
      classes: 'show-Elipses',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: 'ToolModel',
      text: formatMessage('tool_katashiki_model'),
      classes: 'show-Elipses tool-bom-col-tool-model',
      title: (cell: any) => (cell ? String(cell) : undefined),
    },
    {
      dataField: '',
      formatter: CustomActionFormatter(actionIcons),
      text: formatMessage('actions'),
      classes: 'last-column',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ];

  const onPageLimitChanged = (evt: any) => {
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  /**
  * This function is used to update the sort order and reload the list when the grid is sorted, ignoring the initial grid load call.
  * @param type: Grid table change type
  * @param newState: New grid state including sort field and order
  */
  const handleTableChange = (type: any, newState: any) => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    setColumnSortOrder((prevState) => ({
      ...prevState,
      dataField: newState.sortField,
      order: newState.sortOrder,
    }));

    getToolBOMListServiceHandler();
  };

  return (
    <>
      {props.showAddToolBOMPopup && (
        <AddEditToolBOM
          id={selectedId}
          modalClosed={closeModal}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_tool_bom"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_tool_bom_confirm"
          isLoading={deleteToolBOMState.inProgress}
        />
      )}
      {props.showImportToolBOMPopup && (
        <ImportToolBOM
          id={selectedId}
          modalClosed={props.importToggleHandler}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-lg-5 col-md-6 col-sm-6">
            <div className="page_title_wrap">
              <ul className="add-list operator-back">
                <li className="process-chart">
                  <a
                    className="btn btn-secondary"
                    onClick={() => history.push(RoutesConstants.EquipmentManagement)}
                  >
                    <FormattedMessage id="back" />
                  </a>
                </li>
                <li>
                  <h1 className="page_title">
                    <FormattedMessage id="tool_bom" />
                  </h1>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-lg-7 col-md-6 col-sm-6">
            <ul className="add-list">
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={handleRefresh}
                >
                  <RefreshIcon />
                </a>
              </li>
              {isOperationPermittedRoleBase(ModuleType.ToolBOM, 'Add') && (
                <li title={formatMessage('add_tool_bom')}>
                  <a
                    className="btn btn-secondary"
                    onClick={openAddModal}
                  >
                    <span className="me-1">
                      <PlusIcon />
                    </span>
                    <FormattedMessage id="add_tool_bom" />
                  </a>
                </li>
              )}
              {isOperationPermittedRoleBase(ModuleType.ToolBOM, 'Add') && (
                <li title={formatMessage('csv_import')}>
                  <a
                    className="btn btn-light"
                    onClick={openImportModal}
                  >
                    <ImportHolidayIcon />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div className="card card-page">
          {(getAllToolBOMState.inProgress ||
            deleteToolBOMState.inProgress ||
            toolBOMStore.addUpdateToolBOMState.inProgress) && (
            <FormLoader
              loading={
                getAllToolBOMState.inProgress ||
                deleteToolBOMState.inProgress ||
                toolBOMStore.addUpdateToolBOMState.inProgress
              }
            />
          )}
          {getToolBOMList && getToolBOMList.length > 0 ? (
            <>
              <div className="tableDesign row-eq-height tableDevices p-3 changingPanel">
                <div className="table-responsive">
                  <Grid
                    data={getToolBOMList}
                    columns={columns}
                    keyField="Id"
                    loading={getAllToolBOMState.inProgress}
                    hover={true}
                    sort={{ ...columSortOrder }}
                    remote={{ ...GridRemoteConstant }}
                    onTableChange={handleTableChange}
                  />
                </div>
              </div>
              <div className="row no-gutters p-3 pt-0 tableFooter">
                <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                  <FormattedMessage id="show" />
                  <select
                    className="pageLimit"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op: any) => (
                      <option
                        key={op}
                        value={op}
                      >
                        {op}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                  <Pagination
                    isRefreshPage={false}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                    currentPage={pageInfo.currentPage}
                    totalRecords={toolBOMList?.PagingDetails?.TotalRecords || 0}
                  />
                </div>
              </div>
            </>
          ) : (
            !getAllToolBOMState.inProgress && (
              <div className="message">
                <div className="d-flex message-area">
                  <div className="no-data-img">
                    <img
                      src={noMessageIcon}
                      alt=""
                      className="no_message"
                    />
                  </div>
                  <div>
                    <h3 className="fw-bold display-6">
                      <FormattedMessage id="no_record" />
                    </h3>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default AddToolBOMPopupHOC(
  DeletePopupHOC(ImportToolBOMPopupHOC(observer(ToolBOM)))
);
